import { Suspense, useEffect } from 'react';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import SocialProof from './components/SocialProof';
import ProblemStatement from './components/ProblemStatement';
import Features from './components/Features';
import GlobalReach from './components/GlobalReach';
import ChromeExtension from './components/ChromeExtension';
import ATSAutofill from './components/ATSAutofill';
import HowItWorks from './components/HowItWorks';
import Testimonials from './components/Testimonials';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

export default function HomePage() {
  useEffect(() => {
    document.title = 'JobOS — AI Job Search OS | Track, Autofill & Land Jobs Faster';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'JobOS is the world\'s first AI-powered job search operating system. Track every application, autofill 9+ ATS platforms, tailor your resume with AI, and manage your entire job search from one dashboard.');
    setMeta('robots', 'index, follow');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', SITE_URL);

    // Remove any existing page-level JSON-LD scripts
    document.querySelectorAll('script[data-schema-page="home"]').forEach(el => el.remove());

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'JobOS',
        url: SITE_URL,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_URL}/blog?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'JobOS',
        url: SITE_URL,
        logo: `${SITE_URL}/og-image.png`,
        sameAs: ['https://twitter.com/jobos_dev'],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'JobOS',
        url: SITE_URL,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web, Chrome Extension',
        description: 'AI-powered job search operating system with application tracker, ATS autofill, resume builder, and career analytics.',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Is JobOS really free to start?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. The Free plan gives you a fully functional application tracker, resume builder, interview calendar, and Chrome extension for job capture — no credit card required.',
            },
          },
          {
            '@type': 'Question',
            name: 'Which ATS platforms does the autofill support?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'JobOS autofills Workday, Greenhouse, Lever, iCIMS, SmartRecruiters, Workable, Taleo, Rozee.pk, Naukri.com, and any generic HTML form.',
            },
          },
          {
            '@type': 'Question',
            name: 'Does it work outside the US?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'JobOS is built for global job seekers. It works with job boards in Pakistan, India, UAE, UK, Australia, Germany, and all major global ATS platforms.',
            },
          },
          {
            '@type': 'Question',
            name: 'How does AI resume tailoring work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'You build a Master Profile once. When you open a job in the Copilot, it reads the job description, calculates a match score, rewrites your summary, adjusts skills, and generates a tailored cover letter.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is my data private and secure?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. All your data is stored securely and never sold or shared with third parties. We use industry-standard encryption in transit and at rest.',
            },
          },
          {
            '@type': 'Question',
            name: 'What makes JobOS different from Teal, Huntr, or Simplify?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'JobOS is the only platform combining a full application tracker, AI resume tailoring per-job, ATS autofill for 9+ platforms, AI job agents, a recruiter CRM, and a LinkedIn content generator — all in one OS.',
            },
          },
        ],
      },
    ];

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-page', 'home');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema-page="home"]').forEach(el => el.remove());
    };
  }, []);

  return (
    <Suspense fallback={<div className="min-h-screen bg-white dark:bg-[#0A0E1A]"></div>}>
      <div className="min-h-screen bg-white dark:bg-[#0A0E1A]">
        <Navigation />
        <main>
          <Hero />
          <SocialProof />
          <ProblemStatement />
          <Features />
          <GlobalReach />
          <ChromeExtension />
          <ATSAutofill />
          <HowItWorks />
          <Testimonials />
          <Pricing />
          <FAQ />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </Suspense>
  );
}
